import React, { useState, useEffect } from "react";

import { Tabs } from "antd";
import { mapValues } from "lodash";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";

import COA from "./Components/COA";
import Header from "./Components/Common/Header";
import TemplateBuilder from "./Components/TemplateBuilder";
import Templates from "./Components/Templates";
import useTemplateAsyncAction from "./hooks/useTemplateAsyncAction";
import { TEMPLATE_SIDES, TemplateProvider, useTemplateContext } from "./TemplateContext";

import {
  getTemplates, deleteTemplate,
} from "../../../actions/productTemplate";

import "./Components/styles/template.css";
import { getTemplateReportFields } from "./helper";

function TemplateContent() {
  const { withSide, side } = useTemplateContext();
  const [isAddingTemplate, setIsAddingTemplate] = useState(false);
  const [isCreatingCoa, setIsCreatingCoa] = useState(false);
  const [indexOfTemplateSelected, setIndexOfTemplateSelected] = useState(-1);
  const [templateList, setTemplateList] = useState([]);
  const [activeTabKey, setActiveTabKey] = useState("Template");
  const [randomKey, setRandomKey] = useState(0);
  const [customizeReportData, setCustomizeReportData] = useState({});
  const [templateEditing, setTemplateEditing] = useState(null);
  const [productFieldMap, setProductFieldMap] = useState(null);
  const {
    loading: templateLoading,
    run: runListAction,
    abort: abortListAction,
  } = useTemplateAsyncAction();
  const { loadingKey: templateSelectLoadingKey, run: runTemplateSelectAction } = useTemplateAsyncAction();

  /**
   * Fetch the templates list
   */
  const fetchTemplates = () => runListAction(async (signal) => {
    const response = await getTemplates(withSide(), signal);
    if (response?.cancel) {
      return undefined;
    }
    if (!response || !response.success) {
      toast.error("Could not fetch templates.");
      setTemplateList([]);
    } else {
      setTemplateList(response.result);
    }
    return response;
  }, { useAbort: true });

  useEffect(() => {
    const fetchFields = async () => {
      const resp = await getTemplateReportFields(side);
      if (!resp || !resp.success) {
        toast.error("Could not fetch common fields.");
      } else {
        setProductFieldMap(mapValues(resp.fieldsMap, (val) => val.title_field));
      }
    };
    fetchTemplates();
    fetchFields();
    return () => abortListAction();
  }, []); // eslint-disable-line

  const handleAddTemplateClick = () => {
    setCustomizeReportData({});
    setIsAddingTemplate(true);
  };

  /**
   * Fetch template data and open the builder for editing.
   * @param {*} template_id
   * @param {*} signal
   */
  const handleEditTemplate = async (template_id, signal) => {
    const response = await getTemplates(withSide({ template_id }), signal);
    if (response?.cancel || signal?.aborted) {
      return undefined;
    }
    if (response.success) {
      setTemplateEditing(response.result[0]);
      setIsAddingTemplate(true);
    } else {
      toast.error("Could not fetch template");
    }
    return response;
  };

  /**
   * Delete a template.
   * @param {*} template_id
   * @param {*} signal
   */
  const handleDeleteTemplate = async (template_id, signal) => {
    if (signal?.aborted) {
      return undefined;
    }
    const response = await deleteTemplate(template_id, withSide());
    if (signal?.aborted) {
      return undefined;
    }
    if (response.success) {
      toast.success("Successfully deleted template");
      fetchTemplates();
    } else {
      toast.error(response.message);
    }
    return response;
  };

  const clearTemplateSelect = () => {
    setIndexOfTemplateSelected(-1);
  };

  /**
   * Fetch the selected template information
   * @param {*} templateIndex
   */
  const saveTemplateIdForAddingCoa = (templateIndex) => {
    const templateId = templateList[templateIndex]?.template_id;
    return runTemplateSelectAction(async (signal) => {
      if (templateIndex === -1 || templateIndex === indexOfTemplateSelected) {
        clearTemplateSelect();
        return undefined;
      }
      const params = withSide({ template_id: templateId });
      const response = await getTemplates(params, signal);
      if (response?.cancel || signal?.aborted) {
        return undefined;
      }
      if (response && response.success) {
        setIndexOfTemplateSelected(templateIndex);
      } else if (!response || !response.success) {
        toast.error("Could not fetch templates.");
        clearTemplateSelect();
      }
      return response;
    }, { useAbort: true, preventConcurrent: false, key: templateId });
  };

  const handleNavigation = (route) => {
    setCustomizeReportData({});
    /** From TemplateBuilder back to Custom Report */
    if (route === "coacreate") {
      setIsCreatingCoa(() => {
        clearTemplateSelect();
        setIsAddingTemplate(false);
        return true;
      });
    }

    /** After Saving a Custom COA report */
    if (route === "coabuilder") {
      setIsCreatingCoa(() => {
        clearTemplateSelect();
        setIsAddingTemplate(false);
        return false;
      });
    }

    /** From Edit/New template to the Template tab */
    if (route === "builder") {
      fetchTemplates();
      setIsAddingTemplate(false);
      setTemplateEditing(false);
    }
  };

  /**
   * Set the selected samples data from the Custom Report page and redirect to the template builder page
   * @param {*} obj
   */
  const handleCustomizeReportTemplate = (obj) => {
    setCustomizeReportData(obj);
    setIsAddingTemplate(true);
  };

  if (isAddingTemplate || !!templateEditing) {
    const { selectedTemplate, selectedReports } = customizeReportData;
    return (
      <TemplateBuilder
        isCustomizingCoa={!!selectedTemplate && !!selectedReports}
        isEditingTemplate={!!templateEditing}
        selectedReports={selectedReports}
        selectedTemplate={templateEditing || selectedTemplate}
        productFieldMap={productFieldMap}
        handleNavigation={handleNavigation}
      />
    );
  }

  const tabItems = [
    {
      key: "Template",
      label: <Header
        title="Template"
        showCreateReportButton={false}
        activeHeaderIcon={activeTabKey === "Template"}
      />,
      children: <Templates
        templateList={templateList}
        reportIsSelected={false}
        indexOfTemplateSelected={indexOfTemplateSelected}
        handleAddTemplateClick={handleAddTemplateClick}
        saveTemplateIdForAddingCoa={saveTemplateIdForAddingCoa}
        handleEditTemplate={handleEditTemplate}
        handleDeleteTemplate={handleDeleteTemplate}
        templateLoading={templateLoading}
        selectLoadingKey={templateSelectLoadingKey}
        onTemplatesImported={fetchTemplates}
      />,
    },
    {
      key: "Report",
      label: <Header
        title="Customized COA"
        showCreateReportButton={false}
        activeHeaderIcon={activeTabKey === "Report"}
      />,
      children: <COA
        handleCustomizeReportTemplate={handleCustomizeReportTemplate}
        handleIsCreatingCoa={(val) => setIsCreatingCoa(val)}
        isCreatingCoa={isCreatingCoa}
      />,
    },
  ];

  /**
   * While switching different tabs
   *  1. update the active key
   *  2. update the random key to force the tab content rerender
   *  4. fetch the templates list if switch to the Template tab
   *  5. Reset the isCreatingCoa state
   * @param {*} key
   */
  const handleTabOnChange = async (key) => {
    setActiveTabKey(key);
    setRandomKey(uuidv4());
    if (key === "Template") {
      await fetchTemplates();
    }
    setIsCreatingCoa(false);
  };

  return (
    <div className={`TemplateTabsContainer TemplateModule${isCreatingCoa ? " TemplateTabsContainer--createReport" : ""}`}>
      <Tabs
        activeKey={activeTabKey}
        className="TemplateTabs"
        items={tabItems}
        type="card"
        onChange={handleTabOnChange}
        key={randomKey}
      />
    </div>
  );
}

export default function Template({ side = TEMPLATE_SIDES.PRODUCT }) {
  return (
    <TemplateProvider side={side}>
      <TemplateContent />
    </TemplateProvider>
  );
}
